import React, { useState, useEffect } from 'react';
import { Building, CreditCard, Save, Hash, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface BankDetailsSettingsProps {
  profile: any;
  onUpdate?: () => void;
}

export const BankDetailsSettings: React.FC<BankDetailsSettingsProps> = ({
  profile,
  onUpdate
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    bank_account_name: profile?.bank_account_name ?? '',
    bank_sort_code: profile?.bank_sort_code ?? '',
    bank_account_number: profile?.bank_account_number ?? '',
    payment_reference_prefix: profile?.payment_reference_prefix ?? 'THR',
    company_registration_number: profile?.company_registration_number ?? '',
    vat_number: profile?.vat_number ?? '',
    invoice_footer_notes: profile?.invoice_footer_notes ?? ''
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        bank_account_name: profile.bank_account_name ?? '',
        bank_sort_code: profile.bank_sort_code ?? '',
        bank_account_number: profile.bank_account_number ?? '',
        payment_reference_prefix: profile.payment_reference_prefix ?? 'THR',
        company_registration_number: profile.company_registration_number ?? '',
        vat_number: profile.vat_number ?? '',
        invoice_footer_notes: profile.invoice_footer_notes ?? ''
      });
    }
  }, [profile]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('clinician_profiles')
        .update(formData)
        .eq('user_id', user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinician-profile'] });
      toast({
        title: 'Settings saved',
        description: 'Your bank and invoice details have been updated.'
      });
      onUpdate?.();
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  const formatSortCode = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 6);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
  };

  return (
    <Card className="bg-card/50 border-border/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-primary" />
          Bank & Invoice Details
        </CardTitle>
        <CardDescription>
          Configure payment details that appear on invoices
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Bank Details */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium flex items-center gap-2">
            <Building className="h-4 w-4" />
            Bank Account Details
          </h3>
          
          <div className="space-y-2">
            <Label>Account Name</Label>
            <Input
              placeholder="e.g., J Smith Therapy Ltd"
              value={formData.bank_account_name}
              onChange={(e) => setFormData(p => ({ ...p, bank_account_name: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Sort Code</Label>
              <Input
                placeholder="XX-XX-XX"
                value={formData.bank_sort_code}
                onChange={(e) => setFormData(p => ({ 
                  ...p, 
                  bank_sort_code: formatSortCode(e.target.value) 
                }))}
                maxLength={8}
              />
            </div>
            <div className="space-y-2">
              <Label>Account Number</Label>
              <Input
                placeholder="12345678"
                value={formData.bank_account_number}
                onChange={(e) => setFormData(p => ({ 
                  ...p, 
                  bank_account_number: e.target.value.replace(/\D/g, '').slice(0, 8) 
                }))}
                maxLength={8}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Payment Reference Prefix</Label>
            <Input
              placeholder="THR"
              value={formData.payment_reference_prefix}
              onChange={(e) => setFormData(p => ({ 
                ...p, 
                payment_reference_prefix: e.target.value.toUpperCase().slice(0, 5) 
              }))}
              maxLength={5}
            />
            <p className="text-xs text-muted-foreground">
              This prefix will be used in payment references (e.g., THR-CLI-001)
            </p>
          </div>
        </div>

        {/* Business Registration */}
        <div className="space-y-4 pt-4 border-t border-border/30">
          <h3 className="text-sm font-medium flex items-center gap-2">
            <Hash className="h-4 w-4" />
            Business Registration
          </h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Company Registration Number</Label>
              <Input
                placeholder="Optional"
                value={formData.company_registration_number}
                onChange={(e) => setFormData(p => ({ ...p, company_registration_number: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>VAT Number</Label>
              <Input
                placeholder="Optional"
                value={formData.vat_number}
                onChange={(e) => setFormData(p => ({ ...p, vat_number: e.target.value }))}
              />
            </div>
          </div>
        </div>

        {/* Invoice Footer */}
        <div className="space-y-4 pt-4 border-t border-border/30">
          <h3 className="text-sm font-medium flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Invoice Footer
          </h3>
          
          <div className="space-y-2">
            <Label>Footer Notes</Label>
            <Textarea
              placeholder="e.g., Thank you for your business. Payment terms: 14 days."
              value={formData.invoice_footer_notes}
              onChange={(e) => setFormData(p => ({ ...p, invoice_footer_notes: e.target.value }))}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              This text will appear at the bottom of all invoices
            </p>
          </div>
        </div>

        <Button 
          onClick={() => updateMutation.mutate()} 
          disabled={updateMutation.isPending}
          className="w-full gap-2"
        >
          <Save className="h-4 w-4" />
          {updateMutation.isPending ? 'Saving...' : 'Save Invoice Settings'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default BankDetailsSettings;